/**
 * Today's App Growth Brief — the home screen. Three hero numbers (honest
 * empty states until Phase 2 wires app events), the blended ARR dial, alarm
 * tiles, a "needs you" strip, and exactly one recommended decision.
 */
import { Gauge } from "../components/Gauge.js";
import { Bars, Chip, Empty, ErrorNote, Panel, Readout, useApi } from "../components/ui.js";
import { failRateInWindow, fmtDate, fmtMonthDay, int, n, pct, rowsInWindow, usd, PT_ZONE } from "../lib/format.js";
import { DEFAULT_PACING_MODEL, expectedOnPace, paceGap, steadyState, type PacingModel } from "../lib/pacing.js";
import type { ApprovalRow, ArrRow, BudgetRow, CostDay, DeliverabilityResponse, GoalRow, PipelineRow, PriorityRow } from "../lib/types.js";

export function Brief(props: { nav: (page: string) => void }) {
  const arr = useApi<{ arr: ArrRow[] }>("/api/revenue/arr");
  const goals = useApi<{ goals: GoalRow[] }>("/api/goals");
  const approvals = useApi<{ approvals: ApprovalRow[] }>("/api/approvals?status=pending&limit=10");
  const cost = useApi<{ costByDay: CostDay[] }>("/api/agents/cost-by-day");
  const deliv = useApi<DeliverabilityResponse>("/api/deliverability");
  const budgets = useApi<{ budgets: BudgetRow[] }>("/api/budgets");
  const priorities = useApi<{ priorities: PriorityRow[] }>("/api/priorities");
  const pipeline = useApi<{ summary: PipelineRow[] }>("/api/pipeline/summary");
  const ks = useApi<{ paused: boolean }>("/api/killswitch");
  const ctl = useApi<{ forecast: { window: { start: string; active: boolean } | null; projected?: number } }>("/api/controls");

  const goal = goals.data?.goals.find((g) => g.metric === "arr_usd") ?? goals.data?.goals[0] ?? null;
  const model: PacingModel = { ...DEFAULT_PACING_MODEL, ...(goal?.pacing_model ?? {}) };
  const target = n(goal?.target_value) || 1_000_000;

  const prod = (arr.data?.arr ?? []).filter((r) => r.environment === "production");
  const appArr = prod.filter((r) => r.platform === "apple").reduce((s, r) => s + n(r.arr_usd), 0);
  const accelArr = prod.filter((r) => r.platform === "stripe").reduce((s, r) => s + n(r.arr_usd), 0);
  const sandboxRows = (arr.data?.arr ?? []).filter((r) => r.environment === "sandbox");
  const expected = goal
    ? expectedOnPace(target, goal.created_at, goal.target_date ?? "2026-12-31T23:59:59Z", new Date().toISOString())
    : 0;
  const gap = paceGap(appArr + accelArr, expected);
  const steady = steadyState(model, target);

  // 7-day agent ops rollup — windowed by real date, not a fixed row count
  // (a day can have several model rows, so slice(0, N) is the wrong window).
  const costDays = cost.data?.costByDay ?? [];
  const spend7 = rowsInWindow(costDays, 7).reduce((s, r) => s + n(r.total_cost_usd), 0);
  const { rate: failRate, failed: fail7, invocations: inv7 } = failRateInWindow(costDays, 7);

  // Today's sends come from the API's zero-filled `today`, NOT days[0] — the
  // view omits days with no sends, so days[0] is the last send-day (this showed
  // a stale "30" for four days after the 09-05 sends).
  const today = deliv.data?.today ?? null;
  const sentToday = n(today?.sent);
  const repliedContacts = n(pipeline.data?.summary.find((r) => r.status === "replied")?.contacts);
  const pending = approvals.data?.approvals ?? [];
  const noBudgets = budgets.data && budgets.data.budgets.length === 0;

  // Exactly one recommended decision, rule-based.
  let decision: { text: string; cta: string; page: string } | null = null;
  if (noBudgets) {
    decision = { text: "Agent spend enforcement is OFF — there are no budget rows, so runtime.invoke() enforces nothing. Create the first cap.", cta: "Open Agent Ops", page: "agentops" };
  } else if (pending.length > 0) {
    decision = { text: `${pending.length} approval${pending.length > 1 ? "s" : ""} waiting — oldest is ${pending[pending.length - 1]?.kind ?? "?"} #${pending[pending.length - 1]?.id}.`, cta: "Open Approvals", page: "approvals" };
  } else if (failRate > 0.1 && inv7 >= 10) {
    decision = { text: `Agent invocation failure rate is ${pct(failRate)} over the last 7 days. Inspect recent failures before scaling anything.`, cta: "Open Agent Ops", page: "agentops" };
  }

  return (
    <>
      <div className="page-head">
        <h1>Today's App Growth Brief</h1>
        <span className="stamp">{fmtDate(new Date().toISOString())}</span>
        {ks.data?.paused ? <Chip tone="red">⏸ OUTBOUND PAUSED</Chip> : null}
      </div>

      {/* Three hero numbers */}
      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        <Panel delay={0}>
          <Readout hero label="Qualified installs · 7d" value="—" tone="dim" note="app beacon live; qualified-install attribution now channel-level in App Store Connect (Apple campaign links)" />
        </Panel>
        <Panel delay={60}>
          <Readout hero label="Activation rate · first AI analysis ≤48h" value="—" tone="dim" note="pending app integration (Phase 2 event taxonomy)" />
        </Panel>
        <Panel delay={120}>
          <Readout hero label="Accelerator-qualified accounts" value="—" tone="dim" note="internal-only readiness score · Phase 2 v0" />
        </Panel>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        {/* The dial */}
        <Panel title="ARR pacing — $1M by Dec 2026" delay={160}>
          {arr.error ? <ErrorNote error={arr.error} /> : (
            <>
              <Gauge
                actualApp={appArr}
                actualAccel={accelArr}
                expected={expected}
                target={target}
                centerLabel={usd(appArr + accelArr, { compact: true })}
                subLabel={`of ${usd(target, { compact: true })} target`}
              />
              <div className="row" style={{ justifyContent: "center", gap: 18 }}>
                <span className="tag"><span style={{ color: "var(--red)" }}>▮</span> app {usd(appArr, { compact: true })}</span>
                <span className="tag"><span style={{ color: "var(--blue)" }}>▮</span> accelerator {usd(accelArr, { compact: true })}</span>
                <span className="tag"><span style={{ color: "var(--green)" }}>▮</span> on-pace {usd(expected, { compact: true })}</span>
              </div>
              <hr className="rule" />
              <div className="grid cols-3">
                <Readout label="Pace gap" value={usd(gap.gapUsd, { compact: true })} tone={gap.gapUsd >= 0 ? "green" : "amber"} />
                <Readout label="Blended $/account/mo" value={usd(steady.blendedMrrPerAccount)} note={`${pct(model.accel_upsell_rate, 0)} upsell · ${model.accel_lag_months_min}–${model.accel_lag_months_max}mo lag`} />
                <Readout label="Scope accounts at steady state" value={int(steady.accountsNeeded)} note="to carry the full target" />
              </div>
              {sandboxRows.length > 0 ? (
                <div className="note tag" style={{ marginTop: 10 }}>
                  sandbox/testflight data excluded from the dial ({sandboxRows.length} row{sandboxRows.length > 1 ? "s" : ""}) — billing fully wired + sandbox-proven; awaiting App Store approval + first production purchase
                </div>
              ) : null}
            </>
          )}
        </Panel>

        <div className="grid" style={{ alignContent: "start" }}>
          {/* Alarms */}
          {noBudgets ? (
            <Panel alarm title="Alarm — spend enforcement off" delay={200}>
              <span className="blink" style={{ color: "var(--red)" }}>●</span>{" "}
              <span className="dim">agent_budget has zero rows: cost caps are not enforced. Create one in Agent Ops.</span>
            </Panel>
          ) : null}
          {failRate > 0.1 && inv7 >= 10 ? (
            <Panel alarm title="Alarm — agent failure rate" delay={240}>
              <Readout label={`failed / invocations · 7d (${int(fail7)}/${int(inv7)})`} value={pct(failRate)} tone="red" />
            </Panel>
          ) : null}

          {/* KPI strip */}
          <Panel title="Operations strip" delay={280}>
            <div className="grid cols-3">
              <Readout label="Pending approvals" value={int(pending.length)} tone={pending.length > 0 ? "amber" : "green"} />
              <Readout label="Agent spend · 7d" value={usd(spend7)} />
              <Readout label="Contacts replied" value={int(repliedContacts)} note="replies, all-time" />
              <Readout label="Sent today" value={int(sentToday)} note={deliv.data?.dailySendCap ? `cap ${deliv.data.dailySendCap}/day` : undefined} />
              <Readout label="Bounce rate · today" value={sentToday > 0 ? pct(today?.bounce_rate) : "—"} tone={sentToday > 0 && n(today?.bounce_rate) > 0.05 ? "red" : "green"} />
              <Readout label="Kill switch" value={ks.data?.paused ? "PAUSED" : "ARMED·RUN"} tone={ks.data?.paused ? "red" : "green"} />
              <Readout
                label={ctl.data?.forecast.window?.active ? "Sending this window" : "Next window sends"}
                value={ctl.data?.forecast.window ? int(ctl.data.forecast.projected ?? 0) : "—"}
                tone={ctl.data?.forecast.window?.active ? "green" : undefined}
                note={ctl.data?.forecast.window ? new Date(ctl.data.forecast.window.start).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZone: PT_ZONE }) + " PT" : "window closed"}
              />
            </div>
          </Panel>

          {/* Spend chart */}
          <Panel title="LLM spend by day" delay={320}>
            <Bars
              values={aggregateSpend(costDays).map((d) => d.v)}
              labels={aggregateSpend(costDays).map((d) => d.day)}
              format={(v) => usd(v)}
            />
          </Panel>
        </div>
      </div>

      {/* Needs you */}
      <div className="grid cols-2">
        <Panel title="Needs you — one recommended decision" delay={360}>
          {decision ? (
            <div className="row">
              <span style={{ maxWidth: "70%" }}>{decision.text}</span>
              <span className="spacer" />
              <button className="btn primary" onClick={() => props.nav(decision.page)}>{decision.cta}</button>
            </div>
          ) : (
            <Empty>nominal — nothing requires a decision right now</Empty>
          )}
          {pending.length > 0 ? (
            <>
              <hr className="rule" />
              <table className="data">
                <thead><tr><th>#</th><th>kind</th><th>type</th><th>age</th></tr></thead>
                <tbody>
                  {pending.slice(0, 3).map((a) => (
                    <tr key={a.id} className="clickable" onClick={() => props.nav("approvals")}>
                      <td className="num">{a.id}</td>
                      <td><Chip tone="amber">{a.kind}</Chip></td>
                      <td className="dim">{a.action_type ?? "—"}</td>
                      <td className="num dim">{fmtDate(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </Panel>

        <Panel title="Top priorities" delay={400}>
          {priorities.data && priorities.data.priorities.length > 0 ? (
            <table className="data">
              <tbody>
                {priorities.data.priorities.slice(0, 3).map((p, i) => (
                  <tr key={p.id}>
                    <td className="num faint">{String(i + 1).padStart(2, "0")}</td>
                    <td>{p.title}{p.detail ? <div className="faint" style={{ fontSize: 13 }}>{p.detail}</div> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>no priorities set — add them in Goals</Empty>
          )}
        </Panel>
      </div>
    </>
  );
}

/** Collapse per-(day×model) cost rows into per-day totals, oldest→newest. */
function aggregateSpend(rows: CostDay[]): Array<{ day: string; v: number }> {
  const byDay = new Map<string, number>();
  for (const r of rows) {
    const d = fmtMonthDay(r.day);
    byDay.set(d, (byDay.get(d) ?? 0) + n(r.total_cost_usd));
  }
  return [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([day, v]) => ({ day, v }));
}
